"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getLanguageFromLanguageCode = exports.ErrorPage = void 0;
var _layouts = require("@ori-appshell/layouts");
var _shared = require("@ori-appshell/shared");
var _auth = require("@ori/auth");
var _environment = require("@ori/environment");
var _logger = require("@ori/logger");
var _deepmerge = _interopRequireDefault(require("deepmerge"));
var _react = require("react");
var _components = require("../../components");
var _logger2 = require("../../logger");
var _serializeError = require("../../utils/server/serializeError");
var _jsxRuntime = require("react/jsx-runtime");
const isNextBuild = process.env.NEXT_PHASE === 'phase-production-build';
const getLanguageFromLanguageCode = languageCode => languageCode.split('-')[0].toLowerCase();
exports.getLanguageFromLanguageCode = getLanguageFromLanguageCode;
const logger = _logger2.mainLogger.createChild('ErrorPage');

/**
 * Custom Oriflame error page with logic
 *
 * @example
 * ```ts
 * export { ErrorPage as default } from '@ori-appshell/error-page';
 * ```
 */
const ErrorPage = ({
  customerId,
  developmentInfo,
  environment,
  error,
  features,
  language,
  serviceUrls,
  tenant,
  token,
  translations,
  ...props
}) => {
  (0, _react.useEffect)(() => {
    if (error) {
      // This logs both to console and AI
      logger.error(error);
    }
  }, [error]);
  const layoutProps = {
    tenant,
    language,
    features,
    translations,
    serviceUrls,
    token,
    customerId,
    environment,
    developmentInfo
  };
  const contentProps = {
    language,
    environment,
    developmentInfo: environment === 'live' ? undefined : developmentInfo,
    ...props
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_logger.LoggerProvider, {
    logger: _logger2.mainLogger,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_layouts.ErrorPageLayout, {
      ...layoutProps,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.ErrorPageContent, {
        ...contentProps
      })
    })
  });
};
exports.ErrorPage = ErrorPage;
ErrorPage.getInitialProps = async ({
  res,
  err,
  req
}) => {
  const statusCode = err?.statusCode ?? res?.statusCode ?? 500;
  let pageProps = {
    statusCode,
    environment: 'live',
    error: undefined,
    translations: {},
    serviceUrls: {},
    language: _shared.DEFAULT_LANGUAGE,
    tenant: _environment.DEFAULT_TENANT,
    features: [],
    customerId: _auth.NOT_LOGGED_IN_USER_ID,
    token: '',
    developmentInfo: {}
  };

  // If we are on server then execute server-only logic
  if (req) {
    // These imports are necessary so they won't be included in client bundle
    const {
      prepareTenantAndLanguage
    } = await import(/* webpackIgnore: true */'@ori-appshell/routing/edge');
    const {
      appConfig
    } = await import(/* webpackIgnore: true */'@ori-appshell/config/server');
    // eslint-disable-next-line node/file-extension-in-import -- must be used or it wont work in Next.js as the dynamic import is preserved
    const {
      getErrorPageProps
    } = await import(/* webpackIgnore: true */'../../utils/server/getErrorPageProps.js');
    const {
      getDevelopmentInfo
    } = await import(/* webpackIgnore: true */'@ori-appshell/routing');
    if (_logger.ApplicationInsights.isInitialized()) {
      const applicationInsights = _logger.ApplicationInsights.getForServer();
      const {
        operation
      } = applicationInsights.getCorrelationContext();
      pageProps.operationId = operation.id;
    }

    // We don't want to log 404 errors, they are not interesting and they are already tracked through logged requests
    // We also don't want to log errors that are thrown by getStaticProps because they were already logged
    if (statusCode !== 404 && err?.message !== _logger.GET_STATIC_PROPS_ERROR) {
      logger.error(err ?? new Error(`Unknown error happened (HTTP ${statusCode})`));
    }
    try {
      pageProps.developmentInfo = await getDevelopmentInfo();
      const headers = new Headers(Object.entries(req.headers).filter(([, value]) => value !== undefined).map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value]));

      // `prepareTenantAndLanguage` will throw error if it fails to get tenant and language
      // This can happen when request is synthetic, eq. during revalidate when request.url is st like `/internal/something`
      const {
        language = _shared.DEFAULT_LANGUAGE,
        tenant = _environment.DEFAULT_TENANT
      } = await prepareTenantAndLanguage({
        headers,
        url: req.url ?? ''
      });
      const {
        environment,
        features,
        frontendToken,
        serviceUrls,
        translations
      } = await getErrorPageProps({
        tenant,
        pageFeatures: Object.values(_layouts.ErrorPageLayoutFeature),
        language
      });
      if (environment !== 'live' && !isNextBuild // App config crashes inside pipeline builds
      ) {
        // Passing operation resource id causes operation id to be clickable
        // This is only desired on non-live environments
        pageProps.operationResourceId = appConfig.getConfig().AI_RESOURCE_ID;
      }
      pageProps = {
        ...pageProps,
        token: frontendToken,
        features,
        environment,
        serviceUrls,
        language: getLanguageFromLanguageCode(language),
        tenant,
        translations,
        error: environment === 'live' ? undefined : (0, _serializeError.serializeError)(err)
      };
    } catch (error) {
      logger.error(error);
    }
  }

  // This is being run on client ALWAYS (even though error happens on client)
  if (typeof window !== 'undefined') {
    // Get page props like this on client. When error happens on server this getInitialProps is run twice - once on server and once on client.
    // When this function is run again on client it creates new pageProps which on the other hand ignores props from server
    // Since they are still present in document we will extract them and attach them on response payload which has only default values present
    const nextData = window.__NEXT_DATA__.props;

    // data are sometimes nested under page key
    const pagePropsFromServer = nextData.pageProps?.page ?? nextData.pageProps;
    if (pagePropsFromServer?.servicesUrls !== undefined) {
      pagePropsFromServer.serviceUrls = pagePropsFromServer.servicesUrls;
    }
    pageProps = {
      ...(0, _deepmerge.default)(pagePropsFromServer ?? {}, pageProps)
    };

    // This is useful only on client when runtime error happens where appInsights was previously initialized already
    pageProps.operationId ??= window.appInsights?.context.telemetryTrace.traceID;
  }
  return pageProps;
};
//# sourceMappingURL=ErrorPage.js.map